// src/components/admin/AdminOverview.jsx  
import React, { useEffect, useState, useCallback, useMemo } from "react";  
import { useNavigate } from "react-router-dom";  
import { motion } from "framer-motion";  
import {  
  Users, ScrollText, ChevronRight, RefreshCw, TrendingUp,  
  Activity, Key, Bot, Trophy, AlertTriangle, Shield,  
} from "lucide-react";  
import api from "../../api";  
import AdminPageHeader from "./Shared/AdminPageHeader";  
import AdminStatCard from "./Shared/AdminStatCard";  
import AdminIconButton from "./Shared/AdminIconButton";  
import { AdminLoadingState, AdminErrorState, AdminEmptyState } from "./Shared/AdminStates";  
  
const POLL_MS = 15_000; // gentle polling for live counters  
  
const AdminOverview = () => {  
  const navigate = useNavigate();  
  const [loading, setLoading] = useState(true);  
  const [refreshing, setRefreshing] = useState(false);  
  const [error, setError] = useState(null);  
  const [payload, setPayload] = useState(null);  
  const [emergencyOpen, setEmergencyOpen] = useState(false);  
  
  const fetchData = useCallback(async (isRefresh = false) => {  
    try {  
      isRefresh ? setRefreshing(true) : setLoading(true);  
      setError(null);  
      // Larger limit here so top-performer sort has enough data; still cheap.  
      const res = await api.get("/admin/dashboard/overview?page=1&limit=100");  
      setPayload(res.data);  
    } catch (err) {  
      console.error("[AdminOverview]", err);  
      setError(err.response?.data?.detail || "Failed to load dashboard data.");  
    } finally {  
      setLoading(false);  
      setRefreshing(false);  
    }  
  }, []);  
  
  useEffect(() => {  
    fetchData();  
    const id = setInterval(() => fetchData(true), POLL_MS);  
    return () => clearInterval(id);  
  }, [fetchData]);  
  
  const counters = payload?.counters;  
  const rows = payload?.rows || [];  
  
  const recentUsers = useMemo(  
    () => [...rows]  
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))  
      .slice(0, 5),  
    [rows]  
  );  
  
  const topPerformers = useMemo(  
    () => [...rows]  
      .filter((u) => (u.trading?.trade_count || 0) > 0)  
      .sort((a, b) => (b.trading?.total_pnl || 0) - (a.trading?.total_pnl || 0))  
      .slice(0, 5),  
    [rows]  
  );  
  
  // Drift = bot's DB says active but in-memory task is not alive.  
  const driftedAssets = useMemo(() => {  
    const out = [];  
    for (const u of rows) {  
      for (const a of u.assets || []) {  
        if (a.is_active && !a.task_alive) out.push({ user: u, asset: a });  
      }  
    }  
    return out;  
  }, [rows]);  
  
  const goToUserTrades = (u) =>  
    navigate(`/admin/trade-logs?user_id=${u.id}&username=${encodeURIComponent(u.username)}`);  
  
  const emergencyStop = async () => {  
    try {  
      const res = await api.post("/admin/emergency/stop-all-bots");  
      alert(res.data.msg || "All bots stopped.");  
      setEmergencyOpen(false);  
      fetchData(true);  
    } catch (err) {  
      alert("Emergency stop failed: " + (err.response?.data?.detail || err.message));  
    }  
  };  
  
  if (loading) {  
    return (  
      <div className="space-y-6">  
        <AdminPageHeader title="Overview" subtitle="Platform activity at a glance" />  
        <AdminLoadingState label="Loading dashboard…" />  
      </div>  
    );  
  }  
  
  if (error) {  
    return (  
      <div className="space-y-6">  
        <AdminPageHeader title="Overview" subtitle="Platform activity at a glance" />  
        <AdminErrorState message={error} onRetry={fetchData} />  
      </div>  
    );  
  }  
  
  const totalPnl = rows.reduce((s, u) => s + (u.trading?.total_pnl || 0), 0);  
  const profitableUsers = rows.filter((u) => (u.trading?.total_pnl || 0) > 0).length;  
  const withTrades = rows.filter((u) => (u.trading?.trade_count || 0) > 0);  
  const avgWinRate = withTrades.length  
    ? withTrades.reduce((s, u) => s + (u.trading?.win_rate || 0), 0) / withTrades.length  
    : 0;  
  const totalTrades = rows.reduce((s, u) => s + (u.trading?.trade_count || 0), 0);  
  
  return (  
    <div className="space-y-6">  
      <AdminPageHeader  
        title="Overview"  
        subtitle={`Live · updated ${new Date(payload.generated_at).toLocaleTimeString()}`}  
        actions={  
          <>  
            <AdminIconButton  
              onClick={() => fetchData(true)}  
              disabled={refreshing}  
              title="Refresh"  
            >  
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />  
            </AdminIconButton>  
            <button  
              onClick={() => setEmergencyOpen(true)}  
              className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 transition-all"  
              title="Stop every active bot on the platform"  
            >  
              <AlertTriangle size={14} />  
              <span className="hidden sm:inline">Emergency stop</span>  
            </button>  
          </>  
        }  
      />  
  
      {/* Drift banner — surfaces silent DB↔memory mismatch */}  
      {driftedAssets.length > 0 && (  
        <motion.div  
          initial={{ opacity: 0, y: -6 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="flex items-start gap-3 p-4 rounded-xl border border-amber-500/40 bg-amber-500/[0.06]"  
        >  
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />  
          <div className="flex-1 min-w-0">  
            <p className="text-sm font-semibold text-amber-300">  
              {driftedAssets.length} bot config{driftedAssets.length === 1 ? "" : "s"} marked  
              active but not running in memory  
            </p>  
            <p className="text-xs text-amber-200/70 mt-0.5 truncate">  
              {driftedAssets.slice(0, 3).map(d => `${d.user.username}/${d.asset.symbol}`).join(", ")}  
              {driftedAssets.length > 3 && ` +${driftedAssets.length - 3} more`}  
            </p>  
          </div>  
          <button  
            onClick={() => navigate("/admin/users?status=drift")}  
            className="text-xs font-semibold text-amber-300 hover:text-amber-200 flex-shrink-0"  
          >  
            Investigate →  
          </button>  
        </motion.div>  
      )}  
  
      {/* Primary KPIs from server counters (authoritative) */}  
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">  
        <AdminStatCard  
          label="Total users"  
          value={counters.total_users.toLocaleString()}  
          sub={`${counters.active_users} active`}  
          onClick={() => navigate("/admin/users")}  
          index={0}  
        />  
        <AdminStatCard  
          label="Bots running"  
          value={counters.running_configs.toLocaleString()}  
          valueColor={counters.running_configs > 0 ? "text-green-400" : "text-white"}  
          sub={Object.entries(counters.running_per_asset || {})  
            .map(([k, v]) => `${k} ${v}`).join(" · ") || "None"}  
          index={1}  
        />  
        <AdminStatCard  
          label="Total trades"  
          value={totalTrades.toLocaleString()}  
          sub="This page"  
          onClick={() => navigate("/admin/trade-logs")}  
          index={2}  
        />  
        <AdminStatCard  
          label="Platform P&L"  
          value={`${totalPnl >= 0 ? "+" : "−"}$${Math.abs(totalPnl).toFixed(2)}`}  
          valueColor={totalPnl >= 0 ? "text-green-400" : "text-red-400"}  
          sub={`${profitableUsers} profitable users`}  
          index={3}  
        />  
      </div>  
  
      {/* Secondary strip */}  
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">  
        <SecondaryMetric icon={Key} label="Binance connected"  
          value={counters.users_with_binance} accent="blue" />  
        <SecondaryMetric icon={Shield} label="IG connected"  
          value={counters.users_with_ig} accent="gold" />  
        <SecondaryMetric icon={TrendingUp} label="Profitable users"  
          value={profitableUsers} accent="green" />  
        <SecondaryMetric  
          icon={Activity}  
          label="Avg win rate"  
          value={`${avgWinRate.toFixed(1)}%`}  
          accent={avgWinRate >= 50 ? "green" : "gold"}  
        />  
      </div>  
  
      {/* Two-column: Recent + Top */}  
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">  
        <Panel  
          title="Recently joined"  
          icon={Users}  
          action={  
            <button  
              onClick={() => navigate("/admin/users")}  
              className="flex items-center gap-1 text-xs font-semibold text-gold hover:text-yellow-300 transition-colors"  
            >  
              View all <ChevronRight size={14} />  
            </button>  
          }  
        >  
          {recentUsers.length === 0 ? (  
            <AdminEmptyState icon={Users} title="No users yet" />  
          ) : (  
            <div className="space-y-1">  
              {recentUsers.map((u, i) => (  
                <UserRow key={u.id} user={u} onClick={() => goToUserTrades(u)} index={i} />  
              ))}  
            </div>  
          )}  
        </Panel>  
  
        <Panel  
          title="Top performers"  
          icon={Trophy}  
          action={<span className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">By P&L</span>}  
        >  
          {topPerformers.length === 0 ? (  
            <AdminEmptyState icon={TrendingUp} title="No trading activity yet" />  
          ) : (  
            <div className="space-y-1">  
              {topPerformers.map((u, i) => (  
                <UserRow key={u.id} user={u} onClick={() => goToUserTrades(u)} rank={i + 1} index={i} />  
              ))}  
            </div>  
          )}  
        </Panel>  
      </div>  
  
      {/* Emergency confirm */}  
      {emergencyOpen && (  
        <EmergencyConfirm  
          onCancel={() => setEmergencyOpen(false)}  
          onConfirm={emergencyStop}  
          runningCount={counters.running_configs}  
        />  
      )}  
    </div>  
  );  
};  
  
/* ─── Sub-components ─── */  
  
const SecondaryMetric = ({ icon: Icon, label, value, accent }) => {  
  const accents = { green: "text-green-400", blue: "text-blue-400", gold: "text-gold" };  
  return (  
    <motion.div  
      initial={{ opacity: 0, y: 6 }}  
      animate={{ opacity: 1, y: 0 }}  
      className="bg-black/30 border border-gray-800 rounded-xl p-4 flex items-center gap-3"  
    >  
      <div className="w-9 h-9 rounded-lg bg-white/[0.03] border border-gray-800 flex items-center justify-center flex-shrink-0">  
        <Icon size={16} className={accents[accent] || "text-gray-400"} />  
      </div>  
      <div className="min-w-0">  
        <p className="text-lg font-bold text-white leading-none">{value}</p>  
        <p className="text-[11px] text-gray-500 mt-1 truncate">{label}</p>  
      </div>  
    </motion.div>  
  );  
};  
  
const Panel = ({ title, icon: Icon, action, children }) => (  
  <motion.div  
    initial={{ opacity: 0, y: 8 }}  
    animate={{ opacity: 1, y: 0 }}  
    className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-4 sm:p-5"  
  >  
    <div className="flex items-center justify-between mb-4">  
      <div className="flex items-center gap-2">  
        {Icon && <Icon size={16} className="text-gray-500" />}  
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h2>  
      </div>  
      {action}  
    </div>  
    {children}  
  </motion.div>  
);  
  
const UserRow = ({ user, onClick, rank, index }) => {  
  const pnl = user.trading?.total_pnl || 0;  
  const positive = pnl >= 0;  
  const isSuperuser = user.is_superuser;  
  
  const rankBadge = () => {  
    if (!rank) return null;  
    const styles =  
      rank === 1 ? "bg-gold/15 border-gold/40 text-gold" :  
      rank === 2 ? "bg-gray-500/15 border-gray-500/40 text-gray-300" :  
      rank === 3 ? "bg-orange-500/15 border-orange-500/40 text-orange-400" :  
      "bg-white/[0.03] border-gray-800 text-gray-500";  
    return (  
      <span className={`w-6 h-6 rounded-md border flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${styles}`}>  
        {rank}  
      </span>  
    );  
  };  
  
  return (  
    <motion.button  
      initial={{ opacity: 0, x: -8 }}  
      animate={{ opacity: 1, x: 0 }}  
      transition={{ delay: index * 0.03 }}  
      onClick={onClick}  
      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.03] transition-all text-left group"  
    >  
      {rankBadge()}  
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${  
        isSuperuser  
          ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"  
          : "bg-gradient-to-br from-gold to-yellow-600 text-black"  
      }`}>  
        {user.username?.charAt(0).toUpperCase()}  
      </div>  
      <div className="flex-1 min-w-0">  
        <p className="text-sm font-medium text-white truncate group-hover:text-gold transition-colors">  
          {user.username}  
        </p>  
        <p className="text-[11px] text-gray-500 truncate">  
          {rank  
            ? `${(user.trading?.win_rate || 0).toFixed(0)}% win · ${user.trading?.trade_count || 0} trades`  
            : `${user.trading?.trade_count || 0} trades`}  
        </p>  
      </div>  
      <div className="text-right flex-shrink-0">  
        <p className={`text-sm font-bold ${positive ? "text-green-400" : "text-red-400"}`}>  
          {positive ? "+" : "−"}${Math.abs(pnl).toFixed(2)}  
        </p>  
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">USDT</p>  
      </div>  
    </motion.button>  
  );  
};  
  
const EmergencyConfirm = ({ onCancel, onConfirm, runningCount }) => {  
  const [phrase, setPhrase] = useState("");  
  const REQUIRED = "STOP ALL";  
  return (  
    <motion.div  
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}  
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"  
      onClick={onCancel}  
    >  
      <motion.div  
        initial={{ scale: 0.96, y: 8 }} animate={{ scale: 1, y: 0 }}  
        onClick={(e) => e.stopPropagation()}  
        className="bg-gray-900/95 border border-red-500/40 rounded-2xl p-6 max-w-md w-full shadow-2xl"  
      >  
        <div className="flex items-start gap-3 mb-4">  
          <div className="w-11 h-11 rounded-xl bg-red-500/15 border border-red-500/40 flex items-center justify-center flex-shrink-0">  
            <AlertTriangle size={20} className="text-red-400" />  
          </div>  
          <div>  
            <h3 className="text-base font-bold text-white">Emergency stop</h3>  
            <p className="text-sm text-gray-400 mt-1">  
              {runningCount} active bot configuration{runningCount === 1 ? "" : "s"} will be  
              deactivated. Open positions on exchanges are NOT closed.  
            </p>  
          </div>  
        </div>  
        <p className="text-xs text-gray-500 mb-2">  
          Type <span className="font-mono text-red-400">{REQUIRED}</span> to confirm:  
        </p>  
        <input  
          value={phrase}  
          onChange={(e) => setPhrase(e.target.value)}  
          className="w-full px-3 py-2 bg-black/40 border border-gray-800 rounded-lg text-white text-sm font-mono outline-none focus:border-red-500/50"  
          autoFocus  
        />  
        <div className="flex items-center justify-end gap-2 mt-5">  
          <button  
            onClick={onCancel}  
            className="px-4 py-2 text-sm font-semibold text-gray-400 hover:text-white rounded-lg"  
          >  
            Cancel  
          </button>  
          <button  
            onClick={onConfirm}  
            disabled={phrase !== REQUIRED}  
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-400 text-white disabled:opacity-40 disabled:cursor-not-allowed"  
          >  
            Confirm stop  
          </button>  
        </div>  
      </motion.div>  
    </motion.div>  
  );  
};  
  
export default AdminOverview;  